/**
 * Created by rrust on 3/28/18
 */
import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '@polymer/polymer/lib/elements/dom-if.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import template from './ns-input.html';
import {NsTooltip} from '@nuskin/ns-app-elements';
import IMask from 'imask';
import '../ns-form/ns-form.js';
import {isVar} from "babel-types";

/**
 * The ns-input should be used for all inputs at nuskin. It can do lots of cool stuff
 *
 * <h1>Input</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-input></ns-input>
 *   </template>
 * </demo-snippet>
 *
 */
export class NsInput extends PolymerElement {

    static get is() {
        return "ns-input";
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * Used to identify input is in focus or blur based on class styling. Only applies to
             * type="search", where the svg magnifying glass is visible.
             */
            active: {
                type: String,
                value: "active"
            },
            /**
             * Enables/disables browser autocomplete functionality. Options include: "on" and "off" (default).
             * Full list at https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
             */
            autocomplete: {
                type: String,
                value: 'off'
            },
            /**
             * Set this attribute to focus this input when the page renders.
             */
            autofocus: {
                type: Boolean,
                value: false
            },
            /**
             * Will disable input and also apply styling to element.
             */
            disabled: {
                type: Boolean
            },
            /**
             * The text displayed in the tooltip to indicate that the input is disabled.
             */
            disabledTooltipText: {
                type: String,
                value: 'Input Disabled'
            },
            /**
             * Property is used as a flag to track if input element is in focus.
             * By default this is false, however on-focus of input will add active classes to magnifying glass icon.
             * Changing this property on the component makes no change, it is only when focus() runs does it add the active classes.
             */
            focused: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Property will add text below input element. Can be used to clarify or add description to input.
             * By default this is empty and does not display anything.
             */
            helperText: {
                type: String,
                value: ""
            },
            imageObject: {
                type: Object,
                value: function() {
                    return {};
                }
            },
            /**
             * Used by ns-form to show or hide error messages.
             */
            invalid: {
                type: Boolean,
                observer: "showErrors",
                value: false,
                notify: true
            },
            /**
             * Used to when regexValidation is not matched in the input, will display right below input box
             */
            invalidMessage: {
                type: String,
                value: "Does not match regex"
            },
            /**
             * Property populates a label for the input.
             * By default this is empty, and does not display anything.
             */
            label: {
                type: String,
                reflectToAttribute: true
            },
            /**
             * Pattern string where '0' represents a digit, 'a' represents a letter, and '*' represents any character.
             * All other characters are automatically inserted into the mask.
             * For example, 000-000-0000 is a US phone number. The user only has to type the digits. The dashes are
             * inserted automatically.
             * If you want to use a literal 0, a, or *, use \\ to escape. For example, \\0 inserts a '0' character.
             */
            maskPattern: {
                type: String
            },
            /**
             * The placeholder character you want to use in the mask. For example, if you set maskPattern to '000-000-0000'
             * and set maskPlaceholderChar to '_' (underscore), the mask will show ___-___-____.
             */
            maskPlaceholderChar: {
                type: String,
                value: '_'
            },
            /**
             * Sets inputs max allowed length of input, will not let you input more
             */
            maxlength: {
                type: String
            },
            min: Number,
            /**
             * Used for label to work properly.
             */
            name: {
                type: String
            },
            /**
             * Used to bypass the validation
             * Useful if a parent component has its own validation, you can pass the result down
             */
            passedValidation: {
                type: Boolean
            },
            /**
             * The text that the user clicks to hide the password, displayed when the password is visible.
             */
            passwordHideText: {
                type: String,
                value: 'Hide'
            },
            /**
             * The text that the user clicks to show the password, displayed when the password is obscured.
             */
            passwordShowText: {
                type: String,
                value: 'Show'
            },
            /**
             * Indicates whether the password is displayed in clear text (visible) or obscured.
             */
            passwordVisible: {
                type: Boolean,
                value: false
            },
            pattern: String,
            /**
             * Provides a value to the input's placeholder attribute.
             * By default set to an empty string to not display any placeholder.
             */
            placeholder: {
                type: String,
                value: ""
            },
            /**
             * Used to validate the inputs value against.
             */
            regexValidation: {
                type: String
            },
            /**
             * Used to validate the inputs value against for mobile phone.
             */
            regexValidationMobile: {
                type: String
            },
            /**
             * Used to describe the field.
             */
            fieldType: {
                type: String
            },
            /**
             * Used to indicate the input needs to be filled out before submission is allowed.
             */
            required: {
                type: Boolean,
                reflectToAttribute: true
            },
            /**
             * Used when required is true and nothing is in input after on-blur
             */
            requiredMessage: {
                type: String,
                value: "Required"
            },
            state: {
                type: String,
                value: "blur"
            },
            step: Number,
            /**
             * Specifies the type of input this component represents.
             * By default set to 'text', but can also accept 'search' which applies specific
             * styling selectors i.e. show magnifying glass icon
             */
            type: {
                type: String,
                value: "text"
            },
            /**
             * Used to identify if entry is valid
             */
            valid: {
                type: Boolean,
                notify: true
            },
            /**
             * This property contains the input's value, what the user entered into the element.
             * By default this is set to an empty string. Notify property is set to true, to let
             * Polymer know we intended on data-binding this property to other component's.
             */
            value: {
                type: String,
                value: "",
                notify: true,
                reflectToAttribute: true,
                observer: '_valueChanged'
            },
            /**
             * Optional parameter. If set to true, runs validation after debounced by 500ms keyup event (default: false)
             */
            autovalidate: {
                type: Boolean,
                value: false
            },
            /**
             * Additional text to the label to indicate that the field is optional (default: undefined)
             */
            optional: {
                type: String
            },
            /**
             * Set to true if using parent's validation to keep ns-input blur validation from canceling it.
             */
            skipNsInputValidation: {
                type: Boolean
            },
            errorMessage: {
                type: String,
                value: ""
            },

            usingSearchBarComponent: {
                type: Boolean,
                value: false
            },
            /**
             * Capitalize the input
             */
            upperCase: {
                type: Boolean,
                value: false
            },
            /**
             * Dirty flag for this component
             */
            dirty: {
                type: Boolean,
                vale: false,
                notify: true,
                reflectToAttribute: true
            }
        };
    }

    /**
     * returns the updateImage observer function
     */
    static get observers() {
        return [
            '_updateImage(imageObject.url, imageObject.repeat, imageObject.position, imageObject.origin, imageObject.clip, imageObject.style)'
        ];
    }

    ready() {
        super.ready();
        this.changed = false;

        // Workaround for non Chrome browsers that have no support for :host-context yet
        if (window.location.href.indexOf('iw_IL') > 0) {
            if (this.shadowRoot.querySelector('input[type="search"]')) {
                this.shadowRoot.querySelector('input[type="search"]').setAttribute('style', 'padding: 6px 30px 6px 6px;');
            }
            if (this.shadowRoot.querySelector('.input-icon')) {
                this.shadowRoot.querySelector('.input-icon').setAttribute('style', 'right: 8px; left:auto;');
            }
        }

        // This queues up the focus until after the component actually renders, otherwise trying to focus it doesn't work
        afterNextRender(this, () => {
            if (this.autofocus) {
                this.$.input.focus();
            }
        });
    }

    /**
     * Listener method for on-change event
     */
    change() {
        this.valueChanged = true;
        this.dispatchEvent(new CustomEvent('change', {detail: {bubbles: true, change: true, value: this.$.input.value}}));
        if (this.$.input.value === '') {
            this._dispatchValidationEvent();
        }
        if (this.upperCase && this.$.input.value) {
            this.$.input.value = this.$.input.value.toUpperCase();
        }
    }

    /**
     * Listener method for on-focus event
     */
    focus() {
        if (!this.valueChanged) {
            this.origValue = this.$.input.value;
        }
        this.state = "focus";
        this.dispatchEvent(new CustomEvent('focus', {detail: {bubbles: true, focus: true}}));
        this.focused = true;
        this.toggle("active");
        //Start masking when the field has focus
        if (this.maskPattern) this._maskPatternObserver();
    }

    /**
     * Passing the select event on to the input
     */
    select() {
        this.$.input.select();
    }

    /**
     * Listener method for on-keyup event. Applies debounced field validation (500ms)
     */
    keyup() {
        if (this.autovalidate) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = setTimeout(() => {
                this.validate();
            }, 500);
        }
    }

    /**
     * Listener method for on-blur event
     * Flips focused flag, removes styling, and runs validation
     */
    blur() {
        setTimeout(() => {
            this.value = this.value.replace(/\s+/g," ").trim();
            if (this.mask && this.value) {
                this.value = this.value.substring(0, this.maskPattern.length);
            }
            this.state = "blur";
            this.dispatchEvent(new CustomEvent('blur', {detail: {bubbles: true, blur: true}}));

            this.focused = false;


            this.toggle("active");
            if (this.type !== "search" && !this.skipNsInputValidation) {
                this.validate();
                this._dispatchValidationEvent();
            }
            this.$.input.blur();
        }, 300)
    }

    /**
     * Used to toggle styling. Generic toggle method to allow for future possible properties we want to toggle.
     * @param {*} property
     */
    toggle(property) {
        this[property] = (this[property] === property) ? "" : property;
    }

    /**
     * Used to validate the input after a on-blur is fired
     * This function checks if input value passes regexValidation
     * also checks if the field is required it is filled out
     * changes visibility of error element if there is a error
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        // Check if the input is visible
        let state = {};

        this.isValueValid(state);
        this.errorMessage = state.errorMessage || "";
        this.valid = state.valid;
        this.invalid = state.invalid;

        return this.valid;
    }

    isValueValid(state = {}) {
        // Adding a mobile phone validation ability
        if (this.fieldType && this.fieldType == "shippingMobilePhone" && this.regexValidationMobile !== "") {
            this.regexValidation = this.regexValidationMobile;
        }

        // If there is a display: none on the field then don't try to validate it
        if (!this.passedValidation && !(this.attributes.style && this.attributes.style.textContent.indexOf('display: none') > -1)) {
            // handle the case where input is required, needs to match the mask but left unchanged
            if (this.required && this.mask && !this.mask.unmaskedValue) {
                state.errorMessage = this.requiredMessage;
                state.valid = false;
            } else if ((!this.mask || this.mask && this.mask.unmaskedValue) && this.$.input.value && !this.$.input.value.match(this.regexValidation)) {
                state.errorMessage = this.invalidMessage;
                state.valid = false;
            } else if (!this.required || this.required && this.$.input.value) {
                state.errorMessage = "";
                state.valid = true;
            } else {
                state.errorMessage = this.requiredMessage;
                state.valid = false;
            }
        } else {
            state.valid = true;
        }
        state.invalid = !state.valid;

        return state.valid;
    }

    clear() {
        this.errorClass = '';
        this.valid = true;
        this.invalid = false;
        this.errorMessage = '';
        this.dirty = false;
        this.value = '';
        this.origValue = '';
        this.valueChanged = false;
    }

    /**
     * Show errors if invalid. This is used by ns-form for showing or clearing out errors.
     */
    showErrors() {
        const el = this.$.input;
        const errorEl = this.shadowRoot.querySelector("error-tag");
        if (el && el.classList) {
            if (this.invalid) {
                // el.classList.add("error");
                this.errorClass = 'error';
                errorEl.style.display = "block";
                // this.errorMessage = this.invalidMessage;
            } else {
                // el.classList.remove("error");
                errorEl.style.display = "none";
                this.errorMessage = "";
                this.errorClass = '';
            }
        }
    }

    /**
     * Toggles password visibility
     */
    togglePassword() {
        if (this.passwordVisible) {
            this.$.passwordToggle.innerText = this.passwordShowText;
            this.hidePassword();
        } else {
            this.$.passwordToggle.innerText = this.passwordHideText;
            this.showPassword();
        }
        this.passwordVisible = !this.passwordVisible;
    }

    /**
     * Checks to see if the type is password and changes the native input to a
     * type of text so the password is no longer obscured
     */
    showPassword() {
        if (this.type === 'password') {
            this.$.input.type = 'text';
        }
    }

    /**
     * Checks to see if the type is password and changes the native input to a
     * type of password so the password is obscured again
     */
    hidePassword() {
        if (this.type === 'password') {
            this.$.input.type = 'password';
        }
    }

    /**
     * Returns styling information for an image element
     * Currently the if staments are in place to make any rule we dont want to be set an empty string.
     * This is also necessary in order to not break any dependencies but it would be better to just provide styles to input from a string
     * @param {*} url
     * @param {*} repeat
     * @param {*} position
     * @param {*} origin
     * @param {*} clip
     * @param {*} style Extra styles not found elsewhere.
     */
    _updateImage(url, repeat, position, origin, clip, style) {
        let array = [];
        if (url) {
            array.push(`background-image: url('${url}'); `);
        }
        if (repeat) {
            array.push(`background-repeat: ${repeat}; `);
        }
        if (position) {
            array.push(`background-position: ${position}; `);
        }
        if (origin) {
            array.push(`background-origin: ${origin}; `);
        }
        if (clip) {
            array.push(`background-clip: ${clip}; `);
        }
        if (style) {
            array.push(` ${style} `);
        }
        if (array.length) {
            this.set('inputStyle', array.join(''));
        }
    }

    /**
     * Observer for this.maskPattern. Sets up the appropriate mask on the input.
     */
    _maskPatternObserver() {
        /**
         * If there is a maskPattern, maxLength is unnecessary. However, if it is defined, we need to make sure
         * it's larger than the length of the mask - otherwise, we won't be able to type any characters into
         * the input.
         */
        if (this.maxlength && this.maxlength <= this.maskPattern.length) {
            this.maxlength = this.maskPattern.length + 1;
        }
        this.mask = new IMask(
            this.$.input,
            {
                lazy: false,
                mask: this.maskPattern,
                placeholderChar: this.maskPlaceholderChar || 'x'
            }
        );
    }

    /**
     * Observer for this.value. If the input has a mask, this method makes sure that the mask displays the correct
     * value. This is not necessary for someone typing into the input box - the IMask is able to process the
     * characters. However, if the input is in a form, and we cancel the form and then reactivate it, the mask
     * may not work without this method.
     */
    _valueChanged() {
        // I commented this out because it jacks up the field.  If it causes problems with canceling the form and reactivating it then we need another way to do this
        /*if (this.mask) {
            this.mask.value = this.value || '';
            //when you set this.mask.value it cleans up the value.  So we need to set the value back to the cleaned up value applied by mask.value
            this.value = this.mask.value;
        }*/
        if (this.valueChanged) {
            this.dirty = this.$.input.value !== this.origValue;
        }
    }

    _dispatchValidationEvent() {
        this.dispatchEvent(new CustomEvent('input-validation', {detail: {isValid: this.validate(), value: this.$.input.value}, bubbles: true, composed: true}));
    }
}

customElements.define(NsInput.is, NsInput);
